import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export type SmmRole = "smm_admin" | "workplace";

export type SmmMemberRow = {
  user_id: string;
  email: string | null;
  display_name: string | null;
  role: SmmRole;
  created_at: string;
};

async function checkRoles(supabase: any, userId: string) {
  const [sa, adm, isMember, isSmmAdmin] = await Promise.all([
    supabase.rpc("is_super_admin", { _user_id: userId }),
    supabase.rpc("has_role", { _user_id: userId, _role: "admin" }),
    supabase.rpc("is_smm_member", { _user_id: userId }),
    supabase.rpc("is_smm_admin", { _user_id: userId }),
  ]);
  return {
    isSuperAdmin: !!sa.data,
    isPlatformAdmin: !!adm.data || !!sa.data,
    isMember: !!isMember.data,
    isSmmAdmin: !!isSmmAdmin.data,
  };
}

/** Returns the caller's SMM access summary. */
export const getMySmmAccess = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context as any;
    const { isSuperAdmin, isPlatformAdmin, isMember, isSmmAdmin } = await checkRoles(supabase, userId);
    const role: SmmRole | null = isSmmAdmin ? "smm_admin" : isMember ? "workplace" : null;
    return {
      userId,
      isSuperAdmin,
      isMember: isMember || isSuperAdmin || isPlatformAdmin,
      isSmmAdmin: isSmmAdmin || isSuperAdmin || isPlatformAdmin,
      role,
      canManage: isSuperAdmin || isPlatformAdmin || isSmmAdmin,
    };
  });


/** Notify SMM admins + super admins that the caller wants SMM access. */
export const requestSmmAccess = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { note?: string } | undefined) =>
    z.object({ note: z.string().max(500).optional() }).parse(d ?? {}),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context as any;
    const { isMember, isSuperAdmin } = await checkRoles(supabase, userId);
    if (isMember || isSuperAdmin) return { ok: true, already: true };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: requester } = await supabaseAdmin.auth.admin.getUserById(userId);
    const email = requester?.user?.email ?? "(unknown email)";

    const [admins, supers] = await Promise.all([
      supabaseAdmin.from("smm_members").select("user_id").eq("role", "smm_admin"),
      supabaseAdmin.from("user_roles").select("user_id").eq("role", "super_admin"),
    ]);
    const ids = Array.from(
      new Set([
        ...((admins.data ?? []) as any[]).map((r) => r.user_id),
        ...((supers.data ?? []) as any[]).map((r) => r.user_id),
      ]),
    ).filter(Boolean);
    if (!ids.length) return { ok: true, notified: 0 };

    const body = data.note?.trim()
      ? `${email} requested SMM access: "${data.note.trim()}"`
      : `${email} requested SMM access.`;
    const rows = ids.map((user_id) => ({
      user_id,
      title: "SMM access request",
      body,
      link: "/crm/smm/members",
      kind: "smm_access_request",
    }));
    await supabaseAdmin.from("notifications").insert(rows);
    return { ok: true, notified: ids.length };
  });

/** Roster of SMM members (visible to SMM members + super admins). */
export const listSmmMembers = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<SmmMemberRow[]> => {
    const { supabase, userId } = context as any;
    const { isSuperAdmin, isMember } = await checkRoles(supabase, userId);
    if (!isSuperAdmin && !isMember) throw new Error("Forbidden");

    const { data: members, error } = await supabase
      .from("smm_members")
      .select("user_id, role, created_at")
      .order("created_at", { ascending: true });
    if (error) throw new Error(error.message);

    const ids = (members ?? []).map((m: any) => m.user_id);
    if (!ids.length) return [];

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: profiles } = await supabaseAdmin
      .from("profiles")
      .select("user_id, display_name")
      .in("user_id", ids);
    const profileMap = new Map(
      (profiles ?? []).map((p: any) => [p.user_id, p.display_name as string | null]),
    );

    const emailMap = new Map<string, string | null>();
    await Promise.all(
      ids.map(async (id: string) => {
        const { data } = await supabaseAdmin.auth.admin.getUserById(id);
        emailMap.set(id, data?.user?.email ?? null);
      }),
    );

    return (members ?? []).map((m: any) => ({
      user_id: m.user_id,
      role: m.role,
      created_at: m.created_at,
      email: emailMap.get(m.user_id) ?? null,
      display_name: profileMap.get(m.user_id) ?? null,
    }));
  });

/** Add an existing user (by email) to the SMM module. */
export const addSmmMember = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { email: string; role: SmmRole }) =>
    z.object({ email: z.string().email(), role: z.enum(["smm_admin", "workplace"]) }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context as any;
    const { isSuperAdmin, isSmmAdmin } = await checkRoles(supabase, userId);
    if (!isSuperAdmin && !isSmmAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    // Find user by email (paginated lookup since admin API lacks getUserByEmail)
    const targetEmail = data.email.toLowerCase().trim();
    let targetId: string | null = null;
    for (let page = 1; page <= 20 && !targetId; page++) {
      const { data: list, error } = await supabaseAdmin.auth.admin.listUsers({ page, perPage: 200 });
      if (error) throw new Error(error.message);
      const match = list.users.find((u) => (u.email ?? "").toLowerCase() === targetEmail);
      if (match) targetId = match.id;
      if (list.users.length < 200) break;
    }
    if (!targetId) throw new Error(`No user found with email ${data.email}`);

    const { error } = await supabaseAdmin
      .from("smm_members")
      .upsert(
        { user_id: targetId, role: data.role, added_by: userId },
        { onConflict: "user_id" },
      );
    if (error) throw new Error(error.message);
    return { ok: true, user_id: targetId };
  });

export const updateSmmMemberRole = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { user_id: string; role: SmmRole }) =>
    z.object({ user_id: z.string().uuid(), role: z.enum(["smm_admin", "workplace"]) }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context as any;
    const { isSuperAdmin, isSmmAdmin } = await checkRoles(supabase, userId);
    if (!isSuperAdmin && !isSmmAdmin) throw new Error("Forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("smm_members")
      .update({ role: data.role })
      .eq("user_id", data.user_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const removeSmmMember = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { user_id: string }) => z.object({ user_id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context as any;
    const { isSuperAdmin, isSmmAdmin } = await checkRoles(supabase, userId);
    if (!isSuperAdmin && !isSmmAdmin) throw new Error("Forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("smm_members")
      .delete()
      .eq("user_id", data.user_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export type SmmCandidate = {
  user_id: string;
  email: string | null;
  display_name: string | null;
  roles: string[];
};

/** Admins + CRM sales reps who aren't SMM members yet — quick-grant candidates. */
export const listSmmCandidates = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<SmmCandidate[]> => {
    const { supabase, userId } = context as any;
    const { isSuperAdmin, isSmmAdmin } = await checkRoles(supabase, userId);
    if (!isSuperAdmin && !isSmmAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [rolesRes, membersRes] = await Promise.all([
      supabaseAdmin
        .from("user_roles")
        .select("user_id, role")
        .in("role", ["admin", "super_admin", "sales_rep"]),
      supabaseAdmin.from("smm_members").select("user_id"),
    ]);

    const memberIds = new Set(((membersRes.data ?? []) as any[]).map((r) => r.user_id));
    const rolesByUser = new Map<string, Set<string>>();
    for (const r of (rolesRes.data ?? []) as any[]) {
      if (memberIds.has(r.user_id)) continue;
      if (!rolesByUser.has(r.user_id)) rolesByUser.set(r.user_id, new Set());
      rolesByUser.get(r.user_id)!.add(r.role);
    }
    const ids = Array.from(rolesByUser.keys());
    if (!ids.length) return [];

    const { data: profiles } = await supabaseAdmin
      .from("profiles")
      .select("user_id, display_name")
      .in("user_id", ids);
    const profileMap = new Map(
      ((profiles ?? []) as any[]).map((p) => [p.user_id, p.display_name as string | null]),
    );

    const results = await Promise.all(
      ids.map(async (id) => {
        const { data } = await supabaseAdmin.auth.admin.getUserById(id);
        return {
          user_id: id,
          email: data?.user?.email ?? null,
          display_name: profileMap.get(id) ?? null,
          roles: Array.from(rolesByUser.get(id) ?? []),
        } as SmmCandidate;
      }),
    );
    return results.sort((a, b) =>
      (a.display_name ?? a.email ?? "").localeCompare(b.display_name ?? b.email ?? ""),
    );
  });

/** Add an existing user directly by id (used by the candidates picker). */
export const addSmmMemberById = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { user_id: string; role: SmmRole }) =>
    z.object({ user_id: z.string().uuid(), role: z.enum(["smm_admin", "workplace"]) }).parse(d),
  )
  .handler(async ({ context, data }) => {
    const { supabase, userId } = context as any;
    const { isSuperAdmin, isSmmAdmin } = await checkRoles(supabase, userId);
    if (!isSuperAdmin && !isSmmAdmin) throw new Error("Forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("smm_members")
      .upsert(
        { user_id: data.user_id, role: data.role, added_by: userId },
        { onConflict: "user_id" },
      );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export type WorkplaceReport = {
  user_id: string;
  email: string | null;
  display_name: string | null;
  role: SmmRole;
  clients_count: number;
  active_subscriptions: number;
  deals_won: number;
  deals_pending: number;
  pipeline_value_eur: number;
  won_value_eur: number;
};

/** Aggregated per-Workplace-user report (SMM admin sees all, Workplace sees own). */
export const getWorkplaceReports = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { user_id?: string } | undefined) =>
    z.object({ user_id: z.string().uuid().optional() }).parse(d ?? {}),
  )
  .handler(async ({ context, data }): Promise<WorkplaceReport[]> => {
    const { supabase, userId } = context as any;
    const { isSuperAdmin, isSmmAdmin, isMember } = await checkRoles(supabase, userId);
    if (!isSuperAdmin && !isMember) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: members } = await supabaseAdmin
      .from("smm_members")
      .select("user_id, role");

    let targets = (members ?? []) as { user_id: string; role: SmmRole }[];
    if (!isSuperAdmin && !isSmmAdmin) {
      // Workplace user — only their own row
      targets = targets.filter((m) => m.user_id === userId);
    } else if (data.user_id) {
      targets = targets.filter((m) => m.user_id === data.user_id);
    }
    if (!targets.length) return [];

    const ids = targets.map((t) => t.user_id);
    const [profilesRes, emailRes, clientsRes, dealsRes, subsRes] = await Promise.all([
      supabaseAdmin.from("profiles").select("user_id, display_name").in("user_id", ids),
      Promise.all(
        ids.map(async (id) => {
          const { data: u } = await supabaseAdmin.auth.admin.getUserById(id);
          return [id, u?.user?.email ?? null] as const;
        }),
      ),
      supabaseAdmin.from("crm_clients").select("id, assigned_rep").in("assigned_rep", ids),
      supabaseAdmin
        .from("crm_deals")
        .select("status, value_eur, client_id, crm_clients!inner(assigned_rep)")
        .in("crm_clients.assigned_rep", ids),
      supabaseAdmin
        .from("smm_subscriptions")
        .select("id, client_id, status, crm_clients!inner(assigned_rep)")
        .in("crm_clients.assigned_rep", ids),
    ]);

    const profileMap = new Map<string, string | null>(
      ((profilesRes.data ?? []) as any[]).map((p) => [p.user_id, p.display_name]),
    );
    const emailMap = new Map(emailRes);
    const clients = (clientsRes.data ?? []) as { id: string; assigned_rep: string }[];
    const deals = ((dealsRes.data ?? []) as any[]) as {
      status: string;
      value_eur: number | null;
      crm_clients: { assigned_rep: string } | null;
    }[];
    const subs = ((subsRes.data ?? []) as any[]) as {
      status: string;
      crm_clients: { assigned_rep: string } | null;
    }[];

    return targets.map((t) => {
      const userClients = clients.filter((c) => c.assigned_rep === t.user_id);
      const userDeals = deals.filter((d) => d.crm_clients?.assigned_rep === t.user_id);
      const userSubs = subs.filter((s) => s.crm_clients?.assigned_rep === t.user_id);
      return {
        user_id: t.user_id,
        role: t.role,
        email: emailMap.get(t.user_id) ?? null,
        display_name: profileMap.get(t.user_id) ?? null,
        clients_count: userClients.length,
        active_subscriptions: userSubs.filter((s) => s.status === "active").length,
        deals_won: userDeals.filter((d) => d.status === "won").length,
        deals_pending: userDeals.filter((d) => d.status === "pending").length,
        pipeline_value_eur: userDeals
          .filter((d) => d.status === "pending")
          .reduce((sum, d) => sum + Number(d.value_eur ?? 0), 0),
        won_value_eur: userDeals
          .filter((d) => d.status === "won")
          .reduce((sum, d) => sum + Number(d.value_eur ?? 0), 0),
      };
    });
  });

export type WorkplaceUserDetail = {
  user: { user_id: string; email: string | null; display_name: string | null; role: SmmRole };
  clients: { id: string; company_name: string | null; contact_name: string | null; status: string | null }[];
  deals: { id: string; title: string | null; status: string; value_eur: number | null; client_id: string; client_name: string | null; created_at: string }[];
  subscriptions: { id: string; client_id: string; client_name: string | null; status: string; starts_on: string | null; ends_on: string | null }[];
};

/** Drill-down detail for a single Workplace user. */
export const getWorkplaceUserDetail = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { user_id: string }) =>
    z.object({ user_id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ context, data }): Promise<WorkplaceUserDetail> => {
    const { supabase, userId } = context as any;
    const { isSuperAdmin, isSmmAdmin, isMember } = await checkRoles(supabase, userId);
    if (!isSuperAdmin && !isMember) throw new Error("Forbidden");
    if (!isSuperAdmin && !isSmmAdmin && data.user_id !== userId) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const target = data.user_id;

    const [memberRes, profileRes, emailRes, clientsRes, dealsRes, subsRes] = await Promise.all([
      supabaseAdmin.from("smm_members").select("user_id, role").eq("user_id", target).maybeSingle(),
      supabaseAdmin.from("profiles").select("display_name").eq("user_id", target).maybeSingle(),
      supabaseAdmin.auth.admin.getUserById(target),
      supabaseAdmin.from("crm_clients").select("id, company_name, contact_name, status").eq("assigned_rep", target),
      supabaseAdmin
        .from("crm_deals")
        .select("id, title, status, value_eur, client_id, created_at, crm_clients!inner(assigned_rep, company_name)")
        .eq("crm_clients.assigned_rep", target)
        .order("created_at", { ascending: false }),
      supabaseAdmin
        .from("smm_subscriptions")
        .select("id, client_id, status, starts_on, ends_on, crm_clients!inner(assigned_rep, company_name)")
        .eq("crm_clients.assigned_rep", target),
    ]);

    return {
      user: {
        user_id: target,
        email: emailRes.data?.user?.email ?? null,
        display_name: (profileRes.data as any)?.display_name ?? null,
        role: ((memberRes.data as any)?.role as SmmRole) ?? "workplace",
      },
      clients: ((clientsRes.data ?? []) as any[]).map((c) => ({
        id: c.id, company_name: c.company_name, contact_name: c.contact_name, status: c.status,
      })),
      deals: ((dealsRes.data ?? []) as any[]).map((d) => ({
        id: d.id, title: d.title, status: d.status, value_eur: d.value_eur,
        client_id: d.client_id, client_name: d.crm_clients?.company_name ?? null,
        created_at: d.created_at,
      })),
      subscriptions: ((subsRes.data ?? []) as any[]).map((s) => ({
        id: s.id, client_id: s.client_id, status: s.status,
        starts_on: s.starts_on, ends_on: s.ends_on,
        client_name: s.crm_clients?.company_name ?? null,
      })),
    };
  });
